import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { setSeo } from "../lib/seo";
import { api, apiError, track, trackBeacon } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  ["01 · Start Here", "Orientation, workflow, a fictional example and safe AI/calendar guidance."],
  ["02 · Asset Register", "A permanent record of appliances, systems and equipment across the residence."],
  ["03 · Service Cases", "The full lifecycle of each repair — issue, diagnosis, vendor, cost, warranty, follow-up and Principal update."],
  ["04 · Follow-Up Dashboard", "Automatically surfaces active cases needing attention; completed cases leave the view but remain preserved."],
  ["05 · Maintenance Calendar", "A master record of recurring preventive-maintenance obligations. It does not send reminders."],
  ["06 · Vendor Quick Reference", "A lightweight continuity record for the vendors and technicians associated with the residence."],
];

const FLOW = [
  ["Register", "Add permanent asset and equipment information."],
  ["Record", "Open a Service Case when something requires repair or service."],
  ["Follow Up", "Track approvals, diagnosis, parts, vendor responses and warranty."],
  ["Document", "Record completion, invoices, payments, warranties and maintenance."],
  ["Schedule", "Record the next preventive-maintenance date in your own calendar."],
];

export default function ResidenceServiceRecord() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [owned, setOwned] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSeo({ title: "The Residence Service Record · Il Salotto Digitale",
      description: "A professional system for documenting service history, repairs, warranties, follow-up and preventive maintenance across a private residence." });
    track("rsr_view", "residence-service-record");
  }, []);
  useEffect(() => {
    if (user) api.get("/my/courses").then((r) => setOwned(r.data.some((c) => c.id === "residence-service-record"))).catch(() => {});
    else setOwned(false);
  }, [user]);

  const get = async () => {
    track("rsr_cta_click", "residence-service-record");
    if (owned) { nav("/tool/residence-service-record"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { course_id: "residence-service-record", origin_url: window.location.origin });
      trackBeacon("checkout_begin", "residence-service-record");
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const Cta = ({ testid }) => (
    <button onClick={get} disabled={busy} data-testid={testid} className="group inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors disabled:opacity-60">
      {busy ? "Redirecting…" : owned ? "Open the Tool" : "Get the Residence Service Record"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
    </button>
  );

  return (
    <div data-testid="rsr-sales" className="pt-20">
      {/* Hero */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-14 lg:pt-20 pb-12 lg:pb-14 grid lg:grid-cols-12 gap-10 lg:gap-16 lg:items-start">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Use · Professional Tool</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-[4rem] leading-[1.03] tracking-tight">The Residence Service Record</h1>
          </FadeIn>
          <FadeIn delay={0.14}>
            <p className="mt-6 font-serif italic text-xl lg:text-2xl text-ink-soft leading-snug">A repair isn't finished when the technician leaves.</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-5 text-lg font-light text-ink-soft leading-relaxed max-w-xl">A professional system for documenting service history, repairs, warranties, follow-up and preventive maintenance across a private residence.</p>
          </FadeIn>
          <FadeIn delay={0.26}>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <span className="font-serif text-3xl text-ink">$29</span>
              <Cta testid="rsr-cta-top" />
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-ink-soft">One-time purchase · Immediate access</p>
            <p className="mt-3 text-xs font-light text-ink-soft">
              <Link to="/terms" className="link-underline text-ink">Terms</Link>
              <span className="mx-2 text-ink-soft/40">·</span>
              <Link to="/refund-policy" className="link-underline text-ink">Refund Policy</Link>
            </p>
          </FadeIn>
        </div>
        <FadeIn delay={0.2} className="lg:col-span-5">
          <div className="bg-surface/60 border border-line/60 p-4 lg:p-5 shadow-sm">
            <div className="overflow-hidden bg-white border border-line/50 shadow-sm">
              <img src="/rsr_pg3.webp" alt="Inside the Residence Service Record — Quick Start Guide" className="w-full object-cover object-top" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {["/rsr_q-2.webp", "/rsr_pg4.webp", "/rsr_pg5.webp"].map((s) => (
                <div key={s} className="overflow-hidden bg-white border border-line/50 shadow-sm aspect-[3/4]">
                  <img src={s} alt="" className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-ink-soft/70 text-center">Real pages from the Quick Start Guide</p>
          </div>
        </FadeIn>
      </section>

      {/* Story */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-12 lg:py-14">
          <Reveal><MetaLabel className="mb-6">Professional memory for the residence</MetaLabel></Reveal>
          <Reveal delay={0.05}>
            <p className="font-serif font-light text-2xl lg:text-3xl text-ink leading-snug max-w-3xl">If it was never recorded, it becomes memory rather than management.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base font-light text-ink-soft leading-relaxed max-w-2xl">A repair is rarely one appointment — diagnosis today, a part ordered tomorrow, a return visit weeks later. Months on, the real question isn't whether it was fixed, but whether you can retrieve the history: what happened, who handled it, what it cost, what's still under warranty, and what happens next.</p>
          </Reveal>
        </div>
      </section>

      {/* What you receive */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12 py-12 lg:py-16 grid lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-7">
            <Reveal><MetaLabel className="mb-3">What you receive</MetaLabel></Reveal>
            <Reveal delay={0.03}><p className="text-sm font-light text-ink-soft mb-8">Two deliverables, available to download the moment you purchase.</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif font-light text-2xl text-ink">1 · The Excel Workbook <span className="text-ink-soft">— a six-tab professional system</span></h2></Reveal>
            <div className="mt-6 space-y-4">
              {TABS.map(([t, d], i) => (
                <Reveal key={t} delay={0.03 * i}>
                  <div className="flex gap-4 border-b border-line pb-4">
                    <Check size={16} className="mt-1 shrink-0 text-accent" />
                    <div>
                      <p className="font-serif text-lg text-ink">{t}</p>
                      <p className="mt-0.5 text-sm font-light text-ink-soft leading-relaxed">{d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.08}>
              <div className="mt-8">
                <h2 className="font-serif font-light text-2xl text-ink">2 · The Quick Start Guide <span className="text-ink-soft">— seven pages</span></h2>
                <p className="mt-2 text-sm font-light text-ink-soft leading-relaxed max-w-xl">Why the system exists, the five-step workflow, how the six tabs work, a fictional wine-cellar service lifecycle, a ten-minute Friday review, and safe AI + calendar guidance.</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="overflow-hidden bg-surface border border-line/60 shadow-sm">
                <img src="/rsr_q-2.webp" alt="Inside the Quick Start Guide" className="w-full object-cover object-top" />
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-ink-soft/70">From the Quick Start Guide</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="border-t border-line bg-surface/40">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12 py-12 lg:py-16">
          <Reveal><MetaLabel className="mb-3">The workflow</MetaLabel></Reveal>
          <Reveal delay={0.05}><p className="font-serif font-light text-2xl text-ink">Five steps. One continuous record.</p></Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {FLOW.map(([t, d], i) => (
              <Reveal key={t} delay={0.05 * i}>
                <div className="border-t-2 border-ink pt-4">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-accent">Step {i + 1}</p>
                  <p className="mt-2 font-serif text-lg text-ink">{t}</p>
                  <p className="mt-1 text-sm font-light text-ink-soft leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}><p className="mt-8 font-serif italic text-lg text-ink-soft">The repair is complete. The history remains.</p></Reveal>
        </div>
      </section>

      {/* Good to know */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-10">
          <Reveal><p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-3">Good to know</p></Reveal>
          <Reveal delay={0.05}>
            <p className="text-sm font-light text-ink-soft leading-relaxed max-w-3xl">The workbook is your operational record — it doesn't send reminders or emails, contact vendors, or connect to AI, and it doesn't replace your calendar or professional judgment. Your calendar remains the reminder mechanism. If you use an external AI tool to help identify next actions, remove client names, addresses, access, financial and security details first.</p>
          </Reveal>
        </div>
      </section>

      {/* Close */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[900px] px-6 lg:px-12 py-14 lg:py-20 text-center">
          <Reveal><p className="font-serif font-light text-3xl text-ink">Keep the residence's history in one professional system.</p></Reveal>
          <Reveal delay={0.06}>
            <div className="mt-8 flex flex-col items-center gap-4">
              <span className="font-serif text-2xl text-ink">$29 · one-time</span>
              <Cta testid="rsr-cta-bottom" />
              <Link to="/salotto-digitale" className="mt-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">Return to Il Salotto Digitale</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
